<template>
  <span
    :title="!noTitle ? resolvedTitle : undefined"
    class="model-edit-link hover:text-[color-mix(in_srgb,currentColor_75%,transparent)]"
    :class="doubleClick ? '' : 'cursor-pointer'"
    @click="onSingleClick"
    @dblclick="onDoubleClick"
  >
    <slot />
  </span>
</template>

<script setup lang="ts" generic="T extends BaseModel">
import { computed } from "vue"
import { useModelFormStore } from "@/store/model-forms.store"
import type { BaseModel } from "@/models/base.model"
import { ModelFactory } from "@/factories/model-factory.ts"

const props = withDefaults(
  defineProps<{
    /** The model instance to open in the edit form. */
    model: T
    /** Tooltip text shown on hover. Defaults to a click hint that matches the trigger mode. */
    title?: string
    /** Hides the default title tooltip, e.g. to let a parent title show through. */
    noTitle?: boolean
    /** Function to convert the model before opening the edit form (e.g. KnownBusinessEntityContact → Person). */
    modelConversionFunction?: (model: T) => BaseModel | undefined
    /** Callback when the model is saved from the edit form. */
    saveHandle?: (model: BaseModel, isCreating: boolean) => unknown | Promise<unknown>
    /** Callback when the edit form is closed. */
    closeHandle?: () => void
    /** Creation parameters to preset the model form with. */
    creationParameters?: object
    /** Forces a ModelManager update for the converted model after save, useful when a modelConversionFunction is used and the edited model is not managed. */
    forceManagerUpdate?: boolean
    /** Whether to open the edit form on double-click instead of single-click. */
    doubleClick?: boolean
  }>(),
  {
    noTitle: false,
    forceManagerUpdate: false,
    doubleClick: false
  }
)

const emit = defineEmits<{
  saved: [model: BaseModel, isCreating: boolean]
  closed: []
}>()

const modelFormStore = useModelFormStore()
const resolvedTitle = computed(
  () => props.title ?? (props.doubleClick ? "Dubbelklik om te bewerken" : "Klik om te bewerken")
)

function onSingleClick(event: MouseEvent) {
  if (!props.doubleClick) {
    event.stopPropagation()
    onClick()
  }
}

function onDoubleClick(event: MouseEvent) {
  if (props.doubleClick) {
    event.stopPropagation()
    onClick()
  }
}

function onClick() {
  if (!props.model) {
    return
  }

  const modelToEdit = props.modelConversionFunction
    ? props.modelConversionFunction(props.model)
    : props.model

  if (!modelToEdit) {
    return
  }

  modelFormStore.openForm({
    model: modelToEdit,
    creationParameters: props.creationParameters,
    saveHandle: (model: BaseModel, isCreating: boolean) => {
      if (props.forceManagerUpdate) {
        const modelType = model.constructor as any
        ModelFactory.createModel(modelType, model)
      }
      if (props.saveHandle) {
        props.saveHandle(model, isCreating)
      }
      emit("saved", model, isCreating)
    },
    closeHandle: () => {
      if (props.closeHandle) {
        props.closeHandle()
      }
      emit("closed")
    }
  })
}
</script>
